import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { 
  Wind, TrendingUp, TrendingDown, Calendar, MapPin, Target,
  Factory, Zap, Truck, Building2, AlertCircle, CheckCircle,
  BarChart3, PieChart, Activity, Download
} from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';

const EmissionsTracking = () => {
  const [emissionsData, setEmissionsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState('2024');
  const [selectedScope, setSelectedScope] = useState('all');
  const [targetReduction, setTargetReduction] = useState(20); // 20% reduction target
  const [usingRealData, setUsingRealData] = useState(false);

  const fetchEmissionsData = async () => {
    try {
      const data = await apiFetch(`${API}/data/by-source-type/Emissions Data`);
      if (data?._error) {
        console.error('Failed to fetch emissions data:', data.message);
        setEmissionsData(generateMockData());
        setUsingRealData(false);
        setLoading(false);
        return;
      }
      const uploads = data.data;
      
      if (!uploads || uploads.length === 0) {
        console.log('No emissions data uploaded yet, using demo data');
        setEmissionsData(generateMockData());
        setLoading(false);
        return;
      }
      
      // Parse and aggregate emissions data
      const processedData = processEmissionsData(uploads);
      
      // Only use mock data if processed data is empty
      if (processedData.length === 0) {
        setEmissionsData(generateMockData());
        setUsingRealData(false);
      } else {
        setEmissionsData(processedData);
        setUsingRealData(true);
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch emissions data:', error);
      // Use mock data on error
      setEmissionsData(generateMockData());
      setUsingRealData(false);
      setLoading(false);
    }
  };

   
  useEffect(() => {
    fetchEmissionsData();
  }, []);

  const processEmissionsData = (uploads) => {
    // Process uploaded emissions data
    const aggregated = [];
    
    uploads.forEach(upload => {
      // Handle different data structures
      let dataArray = [];
      
      if (upload.data) {
        // If data is already an array
        if (Array.isArray(upload.data)) {
          dataArray = upload.data;
        }
        // If data has sheets (Excel with multiple sheets)
        else if (typeof upload.data === 'object' && upload.data !== null) {
          // Flatten all sheets
          Object.values(upload.data).forEach(sheet => {
            if (Array.isArray(sheet)) {
              dataArray = dataArray.concat(sheet);
            }
          });
        }
      }
      
      dataArray.forEach(record => {
        // Try multiple field name variations
        const scope1 = parseFloat(
          record.scope1 || record.emissions_scope1 || record['Scope 1'] || 
          record.scope_1 || record.Scope1 || 0
        );
        const scope2 = parseFloat(
          record.scope2 || record.emissions_scope2 || record['Scope 2'] || 
          record.scope_2 || record.Scope2 || 0
        );
        const scope3 = parseFloat(
          record.scope3 || record.emissions_scope3 || record['Scope 3'] || 
          record.scope_3 || record.Scope3 || 0
        );
        
        // Only add records with valid emissions data
        if (scope1 > 0 || scope2 > 0 || scope3 > 0) {
          aggregated.push({
            date: record.date || record.month || record.period || record.timestamp || 'Unknown',
            scope1: scope1,
            scope2: scope2,
            scope3: scope3,
            facility: record.facility || record.facility_id || record.location || 'Main',
            source: record.source || record.category || 'Operations'
          });
        }
      });
    });

    return aggregated.length > 0 ? aggregated : generateMockData();
  };

  const generateMockData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.map((month, idx) => ({
      date: `2024-${String(idx + 1).padStart(2, '0')}`,
      month: month,
      scope1: 1200 + Math.random() * 200 - 100,
      scope2: 890 + Math.random() * 150 - 75,
      scope3: 2500 + Math.random() * 400 - 200,
      facility: 'Headquarters',
      source: 'Operations'
    }));
  };

  // Calculate totals and metrics
  const calculateMetrics = () => {
    const total = emissionsData.reduce((acc, curr) => ({
      scope1: acc.scope1 + (curr.scope1 || 0),
      scope2: acc.scope2 + (curr.scope2 || 0),
      scope3: acc.scope3 + (curr.scope3 || 0)
    }), { scope1: 0, scope2: 0, scope3: 0 });

    const totalEmissions = total.scope1 + total.scope2 + total.scope3;
    const avgMonthly = totalEmissions / (emissionsData.length || 1);

    // Calculate year-over-year change (mock -8% improvement)
    const yoyChange = -8.3;

    // Calculate target progress
    const baselineEmissions = totalEmissions * 1.25; // Assume 25% higher baseline
    const targetEmissions = baselineEmissions * (1 - targetReduction / 100);
    const progressPercent = ((baselineEmissions - totalEmissions) / (baselineEmissions - targetEmissions)) * 100;

    return {
      total,
      totalEmissions: totalEmissions.toFixed(0),
      avgMonthly: avgMonthly.toFixed(0),
      yoyChange,
      progressPercent: Math.min(progressPercent, 100).toFixed(1),
      targetEmissions: targetEmissions.toFixed(0),
      baselineEmissions: baselineEmissions.toFixed(0)
    };
  };

  const metrics = calculateMetrics();

  // Prepare chart data
  const monthlyData = emissionsData.map(d => ({
    ...d,
    total: (d.scope1 || 0) + (d.scope2 || 0) + (d.scope3 || 0),
    month: d.month || new Date(d.date).toLocaleDateString('en-US', { month: 'short' })
  }));

  const scopeBreakdown = [
    { name: 'Scope 1 (Direct)', value: metrics.total.scope1, color: '#ef4444' },
    { name: 'Scope 2 (Energy)', value: metrics.total.scope2, color: '#f59e0b' },
    { name: 'Scope 3 (Indirect)', value: metrics.total.scope3, color: '#3b82f6' }
  ];

  const sourceBreakdown = [
    { name: 'Manufacturing', value: 3200, color: '#8b5cf6' },
    { name: 'Transportation', value: 1800, color: '#ec4899' },
    { name: 'Energy', value: 890, color: '#f59e0b' },
    { name: 'Other', value: 650, color: '#6b7280' }
  ];

  const COLORS = ['#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-teal-700 rounded-lg p-8 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wind size={36} />
            <div>
              <h1 className="text-3xl font-bold">Emissions & Carbon</h1>
              <p className="text-green-100">Monitor and manage your carbon footprint across all scopes</p>
            </div>
          </div>
          <div className="flex gap-3">
            <select 
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="px-4 py-2 rounded-lg bg-white text-slate-900 font-semibold"
            >
              <option value="2024">2024</option>
              <option value="2023">2023</option>
              <option value="2022">2022</option>
            </select>
            <button className="px-6 py-2 bg-white text-green-700 rounded-lg font-semibold hover:bg-green-50 flex items-center gap-2">
              <Download size={18} />
              Export Report
            </button>
          </div>
        </div>
      </div>

      {/* Data Source Indicator */}
      {!usingRealData && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle size={20} className="text-yellow-600" />
          <div>
            <p className="text-sm font-semibold text-yellow-900">Demo Data Mode</p>
            <p className="text-xs text-yellow-700">
              No emissions data uploaded yet. Upload emissions data via <a href="/data-upload" className="underline font-semibold">Data Upload</a> to see your actual data.
            </p>
          </div>
        </div>
      )}
      {usingRealData && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle size={20} className="text-green-600" />
          <div>
            <p className="text-sm font-semibold text-green-900">Real Data Connected</p>
            <p className="text-xs text-green-700">
              Showing data from {emissionsData.length} uploaded records. Last updated: {new Date().toLocaleString()}
            </p>
          </div>
        </div>
      )}

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Total Emissions */}
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-600">Total Emissions</h3>
            <Wind className="text-blue-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-slate-900">{metrics.totalEmissions}</p>
          <p className="text-sm text-slate-500">tCO₂e (2024)</p>
        </div>

        {/* YoY Change */}
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-600">YoY Change</h3>
            <TrendingDown className="text-green-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-green-600">{metrics.yoyChange}%</p>
          <p className="text-sm text-slate-500">vs. 2023</p>
        </div>

        {/* Avg Monthly */}
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-600">Avg Monthly</h3>
            <Calendar className="text-orange-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-slate-900">{metrics.avgMonthly}</p>
          <p className="text-sm text-slate-500">tCO₂e per month</p>
        </div>

        {/* Target Progress */}
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-600">Target Progress</h3>
            <Target className="text-purple-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-purple-600">{metrics.progressPercent}%</p>
          <p className="text-sm text-slate-500">{targetReduction}% reduction goal</p>
        </div>
      </div>

      {/* Scope Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Scope 1 */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-red-100 rounded-lg">
              <Factory className="text-red-600" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">Scope 1</h3>
              <p className="text-xs text-slate-500">Direct Emissions</p>
            </div>
          </div>
          <p className="text-2xl font-bold text-slate-900">{metrics.total.scope1.toFixed(0)}</p>
          <p className="text-sm text-slate-600 mt-1">tCO₂e</p>
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Fuel combustion</span>
              <span className="font-semibold">65%</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Company vehicles</span>
              <span className="font-semibold">25%</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Other</span>
              <span className="font-semibold">10%</span>
            </div>
          </div>
        </div>

        {/* Scope 2 */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Zap className="text-orange-600" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">Scope 2</h3>
              <p className="text-xs text-slate-500">Energy Indirect</p>
            </div>
          </div>
          <p className="text-2xl font-bold text-slate-900">{metrics.total.scope2.toFixed(0)}</p>
          <p className="text-sm text-slate-600 mt-1">tCO₂e</p>
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Purchased electricity</span>
              <span className="font-semibold">80%</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Heating/cooling</span>
              <span className="font-semibold">15%</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Other</span>
              <span className="font-semibold">5%</span>
            </div>
          </div>
        </div>

        {/* Scope 3 */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Truck className="text-blue-600" size={24} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">Scope 3</h3>
              <p className="text-xs text-slate-500">Value Chain</p>
            </div>
          </div>
          <p className="text-2xl font-bold text-slate-900">{metrics.total.scope3.toFixed(0)}</p>
          <p className="text-sm text-slate-600 mt-1">tCO₂e</p>
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Supply chain</span>
              <span className="font-semibold">55%</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Business travel</span>
              <span className="font-semibold">30%</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Other</span>
              <span className="font-semibold">15%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp size={20} className="text-blue-600" />
            Monthly Emissions Trend
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="scope1" stackId="1" stroke="#ef4444" fill="#ef4444" name="Scope 1" />
              <Area type="monotone" dataKey="scope2" stackId="1" stroke="#f59e0b" fill="#f59e0b" name="Scope 2" />
              <Area type="monotone" dataKey="scope3" stackId="1" stroke="#3b82f6" fill="#3b82f6" name="Scope 3" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Scope Breakdown Pie */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <PieChart size={20} className="text-purple-600" />
            Emissions by Scope
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPie>
              <Pie
                data={scopeBreakdown}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name.split(' ')[0]}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {scopeBreakdown.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </RechartsPie>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Reduction Initiatives */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Target size={20} className="text-green-600" />
          Active Reduction Initiatives
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border-l-4 border-green-500 bg-green-50 rounded">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={18} className="text-green-600" />
              <h4 className="font-semibold text-slate-900">Renewable Energy</h4>
            </div>
            <p className="text-sm text-slate-600 mb-2">Transition to 100% renewable electricity</p>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">Progress</span>
              <span className="font-semibold text-green-600">65%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
              <div className="bg-green-500 h-2 rounded-full" style={{width: '65%'}}></div>
            </div>
          </div>

          <div className="p-4 border-l-4 border-blue-500 bg-blue-50 rounded">
            <div className="flex items-center gap-2 mb-2">
              <Activity size={18} className="text-blue-600" />
              <h4 className="font-semibold text-slate-900">Fleet Electrification</h4>
            </div>
            <p className="text-sm text-slate-600 mb-2">Replace diesel vehicles with EVs</p>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">Progress</span>
              <span className="font-semibold text-blue-600">40%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
              <div className="bg-blue-500 h-2 rounded-full" style={{width: '40%'}}></div>
            </div>
          </div>

          <div className="p-4 border-l-4 border-orange-500 bg-orange-50 rounded">
            <div className="flex items-center gap-2 mb-2">
              <Building2 size={18} className="text-orange-600" />
              <h4 className="font-semibold text-slate-900">Energy Efficiency</h4>
            </div>
            <p className="text-sm text-slate-600 mb-2">Upgrade HVAC and lighting systems</p>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">Progress</span>
              <span className="font-semibold text-orange-600">55%</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2 mt-2">
              <div className="bg-orange-500 h-2 rounded-full" style={{width: '55%'}}></div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Recommendations */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          <AlertCircle size={20} className="text-purple-600" />
          AI-Powered Recommendations
        </h3>
        <div className="space-y-3">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded">
                <Zap size={18} className="text-green-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-slate-900 mb-1">High Energy Consumption Detected</h4>
                <p className="text-sm text-slate-600">Facility A shows 22% higher energy use than average. Consider energy audit to identify optimization opportunities.</p>
                <span className="text-xs text-green-600 font-semibold mt-2 inline-block">Potential savings: 180 tCO₂e/year</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded">
                <Truck size={18} className="text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-slate-900 mb-1">Supply Chain Optimization</h4>
                <p className="text-sm text-slate-600">Route optimization for logistics could reduce Scope 3 emissions by 12%. Implement AI-driven route planning.</p>
                <span className="text-xs text-blue-600 font-semibold mt-2 inline-block">Potential savings: 300 tCO₂e/year</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmissionsTracking;
